import { useContext, useCallback, createContext } from 'react';

import {
  clientErrorTracker,
  useSoyuzClientConfig,
  useSoyuzFlag,
  useSoyuzLocales
} from '@spotahome/soyuz/client';

function getRecaptchaEnterprise() {
  const g = window?.grecaptcha;
  return g?.enterprise;
}

const RECAPTCHA_NET_HOST = 'https://www.recaptcha.net';

const ReCaptchaContext = createContext(null);

export function ReCaptchaProvider({ children }) {
  const { recaptchaSiteKey } = useSoyuzClientConfig();
  const { current: locale } = useSoyuzLocales();

  const loadRecaptcha = useCallback(async () => {
    if (getRecaptchaEnterprise()) {
      return;
    }

    const qs = new URLSearchParams({ render: recaptchaSiteKey, hl: locale });
    const url = `${RECAPTCHA_NET_HOST}/recaptcha/enterprise.js?${qs.toString()}`;
    const script = document.createElement('script');
    script.src = url;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }, []);

  const executeRecaptcha = useCallback(
    async action => {
      if (!recaptchaSiteKey) {
        throw new Error('Missing reCAPTCHA site key.');
      }

      const api = getRecaptchaEnterprise();

      if (!api?.execute) {
        throw new Error('reCAPTCHA Enterprise API not available.');
      }

      if (!action) {
        throw new Error(
          'Missing reCAPTCHA action. Pass an action to executeRecaptcha(action).'
        );
      }

      return new Promise((resolve, reject) => {
        api.ready(() => {
          api.execute(recaptchaSiteKey, { action }).then(resolve).catch(reject);
        });
      });
    },
    [recaptchaSiteKey]
  );

  return (
    <ReCaptchaContext.Provider value={{ executeRecaptcha, loadRecaptcha }}>
      {children}
    </ReCaptchaContext.Provider>
  );
}

export function useReCaptcha(action) {
  const TenantRecaptchaFlag = useSoyuzFlag('TenantRecaptcha');
  const { executeRecaptcha, loadRecaptcha } = useContext(ReCaptchaContext);

  if (TenantRecaptchaFlag.isOn()) {
    loadRecaptcha();
  }

  return {
    executeRecaptcha: async () => {
      if (TenantRecaptchaFlag.isOff()) {
        return null;
      }

      let recaptchaToken = null;

      try {
        recaptchaToken = await executeRecaptcha(action);
      } catch (error) {
        clientErrorTracker.error({
          error,
          source: `ReCaptchaContext-useExecuteRecaptcha-${action}`
        });
      }

      return recaptchaToken;
    }
  };
}

export default ReCaptchaProvider;
